import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ExternalLink,
  Filter as FilterIcon,
  Maximize2,
  Plus as PlusIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  X as XIcon,
} from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import type { BrandInsightsPageContext, Chat } from '@trinity/chat';
import { BiChatContextType, ChatContextType } from '@trinity/chat';
import {
  AuraChatContext,
  BrandInsightsModalContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat';
import {
  ActiveChatSessionContext,
  AuthorContext,
  ChatPageContextDataContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { calculateActiveFiltersCount } from '@trinity/chat-components/components/Chat.Filters/common';
import { FilterPaneWorkspace } from '@trinity/chat-components/components/Chat.Filters/FilterPane.Workspace';
import { useHeaderContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ContextSearch } from '@trinity/chat-components/components/Chat.Header/ContextSearch';
import { ChatContextSearchContainer } from '@trinity/chat-components/components/Chat.Header/ContextSearch.Container';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container';
import { SimpleContextSelector } from '@trinity/chat-components/components/Chat.Header/SimpleContextSelector';
import {
  ChatHistory,
  ChatHistoryItemsContext,
  ChatHistorySharedContext,
} from '@trinity/chat-components/components/Chat.History/index.js';
import { SidePanelToggleContext } from '@trinity/chat-components/components/Chat.SidePanel/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { RateLimitingContext } from '@trinity/chat-components/containers/RateLimiting/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';

// ─── Sparkle SVG (Figma: #565ADD) ────────────────────────────────────────────
const SparkleIcon = () => <AutoAwesomeOutlinedIcon style={{ width: 18, height: 18, color: '#565ADD' }} />;

// ─── Hamburger SVG — three bars of decreasing width (Figma: #565ADD) ─────────
const HamburgerSVG = () => (
  <svg width="21" height="14" viewBox="0 0 21 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="21" height="2" rx="1" fill="#565ADD" />
    <rect y="6" width="16.8" height="2" rx="1" fill="#565ADD" />
    <rect y="12" width="11.2" height="2" rx="1" fill="#565ADD" />
  </svg>
);

// ─── Settings sub-panel ───────────────────────────────────────────────────────
const SidePanelSettingsView = () => {
  const user = useContext(AuthorContext);
  const { query } = useContext(RateLimitingContext);

  const stats = useMemo(() => {
    const rawLimit = query?.data?.limit;
    const limit = rawLimit === 0 ? 5000 : (rawLimit ?? null);
    return { current: query?.data?.current ?? 0, limit };
  }, [query?.data]);

  const limitDisplay = stats.limit === null ? 'No Preset Limit' : stats.limit.toLocaleString();

  return (
    <FullPanelView>
      <SettingsContent>
        <SettingsTitle>Chat Settings</SettingsTitle>
        <SettingsRow>{`Username: ${user?.profile?.email ?? '—'}`}</SettingsRow>
        <SettingsRow>{`Submitted Chats: ${stats.current.toLocaleString()} / ${limitDisplay}`}</SettingsRow>
      </SettingsContent>
    </FullPanelView>
  );
};

// ─── History sub-panel ────────────────────────────────────────────────────────
type HistoryViewProps = {
  items: Chat.Instance[];
  shared: Chat.Instance[];
  onNewChat: () => void;
  onSettings: () => void;
  isRateLimitExceeded: boolean;
};

const SidePanelHistoryView = ({
  items,
  shared,
  onNewChat,
  onSettings,
  isRateLimitExceeded,
}: HistoryViewProps) => (
  <FullPanelView>
    <HistoryNewChatRow>
      <NewChatPill disabled={isRateLimitExceeded} onClick={onNewChat}>
        <PlusIcon size={14} />
        New Chat
      </NewChatPill>
    </HistoryNewChatRow>
    <HistoryScrollArea>
      <ChatHistory items={items} shared={shared} />
    </HistoryScrollArea>
    <HistoryFooter>
      <SettingsGearButton onClick={onSettings} title="Chat Settings" aria-label="Chat Settings">
        <SettingsIcon size={20} />
      </SettingsGearButton>
    </HistoryFooter>
  </FullPanelView>
);

// ─── Main component ───────────────────────────────────────────────────────────
export const ChatSidePanel = () => {
  const pageContext = useContext(ChatPageContextDataContext);
  const chat = useContext(ActiveChatSessionContext);
  const [isExpanded, setIsExpanded] = useContext(SidePanelExpandedContext);
  const { closePanel } = useContext(SidePanelToggleContext);
  const [, toggleModal] = useContext(BrandInsightsModalContext);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const items = useContext(ChatHistoryItemsContext);
  const shared = useContext(ChatHistorySharedContext);

  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const chatWelcomeScreen = useContext(ChatWelcomeScreenContext);
  const { query } = useContext(RateLimitingContext);
  const { setAuraChatStatus } = useContext(AuraChatContext);
  const defaultPanelWidth = 435;
  const [width, setWidth] = useState(defaultPanelWidth);
  const wrapRef = useRef<HTMLDivElement>(null);

  const filters = useContext(ChatSessionFiltersContext);
  const features = useContext(GroupFeaturesContext);
  const resolvedFeatures = useMemo(
    () => ({
      hcpProfiles: features?.hcpProfiles ?? false,
      workspaces: features?.workspaces ?? false,
      engagementFilter: features?.engagementFilter ?? false,
    }),
    [features?.hcpProfiles, features?.workspaces, features?.engagementFilter]
  );

  const activeFiltersCount = useMemo(() => {
    const currentContext = chat?.context ?? pageContext;
    const baseCount = calculateActiveFiltersCount({
      context: currentContext,
      data: filters,
      features: resolvedFeatures,
    });

    if (!currentContext) {
      return baseCount;
    }

    const hasProjectInFilters = filters.projects.length > 0;
    const hasContextProject =
      currentContext.type === ChatContextType.Project ||
      (currentContext.type === ChatContextType.Agent &&
        currentContext.data.identifier === BiChatContextType.Project);

    if (!hasProjectInFilters && hasContextProject) {
      return baseCount + 1;
    }

    return baseCount;
  }, [chat?.context, pageContext, filters, resolvedFeatures]);

  const handleClosePanel = useCallback(() => {
    closePanel();
    setIsExpanded(false);
  }, [closePanel, setIsExpanded]);

  const handleNewChat = useCallback(() => {
    setAuraChatStatus(false);
    chatWelcomeScreen?.dismiss();
    setShowHistory(false);
    setShowSettings(false);
    setShowFilters(false);

    const context = chat?.context
      ? { ...chat.context, immutableFilters: pageContext?.immutableFilters }
      : pageContext;

    if (!context) return;

    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    if (!brandIdentifier) {
      console.warn('Brand identifier is required to start a new chat');
      return;
    }

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [setAuraChatStatus, chatWelcomeScreen, dispatch, chat, pageContext, brandIdentifier]);

  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const minPanelWidth = 300;
  const minVisibleMainContentRatio = 0.2; // Keep at least 20% for main content area
  const maxPanelWidthRatio = 0.3; // Cap panel to max 30% of screen

  const getPanelWidthBounds = useCallback(() => {
    const containerWidth =
      wrapRef.current?.closest('[data-panel-layout-container]')?.getBoundingClientRect().width ??
      window.innerWidth;
    // In in-flow layout mode, keep at least 20% of the main area visible.
    const minVisibleMainContentWidth = containerWidth * minVisibleMainContentRatio;
    const maxWidthFromMainAreaFloor = containerWidth - minVisibleMainContentWidth;
    const maxWidthFromPanelCeiling = containerWidth * maxPanelWidthRatio;
    const maxPanelWidth = Math.min(maxWidthFromMainAreaFloor, maxWidthFromPanelCeiling);

    return {
      min: minPanelWidth,
      max: Math.max(minPanelWidth, maxPanelWidth),
    };
  }, [minPanelWidth, minVisibleMainContentRatio, maxPanelWidthRatio]);

  // Auto-set context to the current page when the panel transitions closed → open.
  // This ensures the chat context matches the page (e.g. opening the panel on a Project
  // page starts a project-scoped chat) without disrupting chats that already match.
  const wasExpandedRef = useRef(false);
  useEffect(() => {
    const justOpened = isExpanded && !wasExpandedRef.current;
    wasExpandedRef.current = isExpanded;

    if (!justOpened || !pageContext || !brandIdentifier) return;
    if (pageContext.type === ChatContextType.Global) return;
    if (chat?.context?.type === pageContext.type) return;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(pageContext, false),
        brandIdentifier,
        resetLandingCards: false,
      },
    });
  }, [isExpanded, pageContext, brandIdentifier, chat?.context?.type, dispatch]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const clampCurrentWidth = () => {
      setWidth((currentWidth) => {
        const { min, max } = getPanelWidthBounds();
        return Math.max(min, Math.min(max, currentWidth));
      });
    };

    clampCurrentWidth();
    window.addEventListener('resize', clampCurrentWidth);
    return () => window.removeEventListener('resize', clampCurrentWidth);
  }, [getPanelWidthBounds, isExpanded]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = wrapRef.current?.offsetWidth || 0;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const diff = startX - moveEvent.clientX;
        const nextWidth = startWidth + diff;
        const { min, max } = getPanelWidthBounds();
        setWidth(Math.max(min, Math.min(max, nextWidth)));
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        resizeCleanupRef.current = null;
      };

      resizeCleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [getPanelWidthBounds]
  );

  const isRateLimitExceeded = useMemo(() => query?.data?.allowed === false, [query?.data?.allowed]);

  if (!isExpanded) return null;

  const inSubView = showHistory || showSettings;

  return (
    <Root>
      <ResizeHandle onMouseDown={handleResizeStart} />
      <Wrap customWidth={width} ref={wrapRef}>
        {/* ── Header (65px, always visible) ── */}
        <Header>
          <Menubar>
            <HamburgerButton
              onClick={() => {
                setShowFilters(false);
                if (showSettings) {
                  setShowSettings(false);
                  setShowHistory(true);
                  return;
                }
                if (showHistory) {
                  setShowHistory(false);
                  setShowSettings(false);
                  return;
                }
                setShowHistory(true);
              }}
              title={inSubView ? 'Back to chat' : 'Chat history'}
              aria-label={inSubView ? 'Back to chat' : 'Chat history'}
            >
              {inSubView ? (
                <BackArrowLabel>
                  <ChevronLeft size={14} strokeWidth={2.5} />
                  Back
                </BackArrowLabel>
              ) : (
                <HamburgerSVG />
              )}
            </HamburgerButton>

            <TitleGroup>
              <SparkleIcon />
              <TitleText>Insights AI</TitleText>
            </TitleGroup>

            <HeaderIcons>
              {/* NorthEast: hidden per PRD §18.2 (future "open in new window") */}
              <FlatIconButton disabled aria-label="Open in new window" style={{ visibility: 'hidden' }}>
                <ExternalLink size={14} />
              </FlatIconButton>
              <FlatIconButton
                onClick={() => {
                  toggleModal();
                  closePanel();
                }}
                aria-label="Expand to modal"
              >
                <Maximize2 size={14} />
              </FlatIconButton>
              <FlatCloseButton onClick={handleClosePanel} aria-label="Close panel">
                <XIcon size={14} />
              </FlatCloseButton>
            </HeaderIcons>
          </Menubar>
          <HeaderDivider />
        </Header>

        {/* ── Content area ── */}
        {renderContent()}
      </Wrap>
    </Root>
  );

  function renderContent() {
    if (showSettings) {
      return <SidePanelSettingsView />;
    }
    if (showHistory) {
      return (
        <SidePanelHistoryView
          items={items}
          shared={shared}
          onNewChat={handleNewChat}
          onSettings={() => setShowSettings(true)}
          isRateLimitExceeded={isRateLimitExceeded}
        />
      );
    }
    return (
      <MainContent>
        {/* ── Topic container (context selector + filter + new chat) ── */}
        <TopicWrapper>
          <ContextSelectorContainer>
            <SidePanelTopicArea
              activeFiltersCount={activeFiltersCount}
              isRateLimitExceeded={isRateLimitExceeded}
              onFilterClick={() => setShowFilters((prev) => !prev)}
              onNewChat={handleNewChat}
              pageContext={pageContext}
            />
          </ContextSelectorContainer>
        </TopicWrapper>
        {/* Chat area: position:relative so the filter overlay anchors to it */}
        <ChatArea>
          {showFilters && (
            <SidePanelFilterView
              active={activeFiltersCount > 0}
              onClose={() => setShowFilters(false)}
              onFiltersConfirmed={handleNewChat}
            />
          )}
          <ChatInstance isSidePanel={true} />
        </ChatArea>
      </MainContent>
    );
  }
};

ChatSidePanel.displayName = 'Chat.SidePanel';

// ─── SidePanelTopicArea ──────────────────────────────────────────────────────
// Rendered inside ContextSelectorContainer so useHeaderContext() is available.

type SidePanelTopicAreaProps = {
  activeFiltersCount: number;
  isRateLimitExceeded: boolean;
  onFilterClick: () => void;
  onNewChat: () => void;
  pageContext: BrandInsightsPageContext.Item;
};

function SidePanelTopicArea({
  activeFiltersCount,
  isRateLimitExceeded,
  onFilterClick,
  onNewChat,
  pageContext,
}: SidePanelTopicAreaProps) {
  const { setPendingContextType, setPendingEverythingSearch, pendingContextType, pendingEverythingSearch } =
    useHeaderContext();

  const hasActiveSearch = !!(pendingContextType ?? pendingEverythingSearch);

  const handleSearchClick = useCallback(() => {
    if (!pageContext || pageContext.type === ChatContextType.Global) {
      setPendingEverythingSearch(true);
      return;
    }

    if (pageContext.type === ChatContextType.Agent) {
      // Map BiChatContextType identifiers to the corresponding ChatContextType so the
      // ContextSearch placeholder shows e.g. "Search projects" rather than "Search Anything".
      const biTypeToContextType: Partial<Record<string, ChatContextType>> = {
        [BiChatContextType.Project]: ChatContextType.Project,
        [BiChatContextType.Brand]: ChatContextType.Brand,
        [BiChatContextType.Transcript]: ChatContextType.Transcript,
        [BiChatContextType.Document]: ChatContextType.Document,
        [BiChatContextType.Folder]: ChatContextType.Folder,
      };
      const mapped = biTypeToContextType[pageContext.data.identifier];
      if (mapped) {
        setPendingContextType(mapped);
      } else {
        setPendingEverythingSearch(true);
      }
      return;
    }

    setPendingContextType(pageContext.type);
  }, [pageContext, setPendingContextType, setPendingEverythingSearch]);

  return (
    <TopicRow>
      <TopicInner>
        <ChatContextSearchContainer>
          {hasActiveSearch ? (
            <StyledContextSearch />
          ) : (
            <>
              <ContextSelectorWrapper>
                <SimpleContextSelector />
              </ContextSelectorWrapper>
              <TopicIconButton onClick={handleSearchClick} aria-label="Search context">
                <SearchIcon size={17} color="#565ADD" />
              </TopicIconButton>
            </>
          )}
        </ChatContextSearchContainer>
        <SidePanelFilterButton
          count={activeFiltersCount}
          onClick={onFilterClick}
          aria-label="Source filters"
        />
      </TopicInner>
      <NewChatCircle disabled={isRateLimitExceeded} onClick={onNewChat} aria-label="New chat">
        <StyledPlusIcon />
      </NewChatCircle>
    </TopicRow>
  );
}

// ─── Filter overlay ───────────────────────────────────────────────────────────
type FilterViewProps = {
  active: boolean;
  onClose: () => void;
  onFiltersConfirmed: () => void;
};

const SidePanelFilterView = ({ active, onClose, onFiltersConfirmed }: FilterViewProps) => {
  const dispatch = useContext(ChatStateDispatchContext);
  const filters = useContext(ChatSessionFiltersContext);

  // Capture filter state when overlay opens to detect changes on close
  const filtersAtOpenRef = useRef(filters);

  const handleClickAway = useCallback(() => {
    if (filtersAtOpenRef.current !== filters) {
      onFiltersConfirmed();
    }
    onClose();
  }, [filters, onClose, onFiltersConfirmed]);

  const handleReset = useCallback(() => {
    dispatch({ type: 'filters/reset' });
  }, [dispatch]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FilterOverlay>
        <FilterViewHeader>
          <FilterViewTitle>Source Filters</FilterViewTitle>
          <FilterResetButton disabled={!active} onClick={handleReset}>
            Reset
          </FilterResetButton>
        </FilterViewHeader>
        <FilterPaneWorkspace active={active} isSidePanel={true} />
      </FilterOverlay>
    </ClickAwayListener>
  );
};

// ─── Styled components (object syntax per AGENTS.md) ─────────────────────────

const Root = styled('div')({
  boxSizing: 'border-box',
  height: '100%',
  position: 'relative',
  display: 'flex',
});

const Wrap = styled('div')<{ customWidth: number }>(({ customWidth, theme }) => ({
  position: 'relative',
  width: `${customWidth}px`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#FFFFFF',
  borderLeft: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  borderRadius: 0,
  overflow: 'hidden',
  overscrollBehavior: 'contain',
}));

const ResizeHandle = styled('div')({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 4,
  height: '100%',
  cursor: 'col-resize',
  background: 'transparent',
  zIndex: 100,
  '&:hover': { background: '#0078d4' },
});

// ── Header ───────────────────────────────────────────────────────────────────

const Header = styled('div')({
  width: '100%',
  flexShrink: 0,
});

const Menubar = styled('div')({
  height: 65,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 14px',
  background: '#FFFFFF',
  boxSizing: 'border-box',
});

const HamburgerButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  borderRadius: 4,
  '&:hover': { background: '#F5F5F5' },
});

const TitleGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flex: 1,
  justifyContent: 'center',
});

const TitleText = styled('span')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  color: '#121217',
  userSelect: 'none',
}));

// Shown in place of the hamburger icon when in a sub-view (history/settings)
const BackArrowLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  color: theme.palette.sentiment.primary.main,
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
}));

const HeaderIcons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
});

const FlatIconButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  color: '#18181B',
  '&:hover:not(:disabled)': { background: '#F5F5F5' },
  '&:disabled': { cursor: 'default' },
});

const FlatCloseButton = styled(FlatIconButton)({
  border: 'none',
  borderRadius: 3,
  width: 20,
  height: 20,
  padding: 2,
});

const HeaderDivider = styled('div')({
  height: 1,
  background: '#E6E6E6',
  margin: '0 14px',
});

// ── Topic container ───────────────────────────────────────────────────────────

type SidePanelFilterButtonProps = {
  count: number;
  onClick: () => void;
  'aria-label'?: string;
};

const SidePanelFilterButton = ({ count, onClick, 'aria-label': ariaLabel }: SidePanelFilterButtonProps) => {
  const isActive = count > 0;
  return (
    <FilterSquareButton onClick={onClick} aria-label={ariaLabel}>
      <FilterIconWrap>
        <FilterIcon size={16} color="#FFFFFF" />
        {isActive && <FilterActiveBadge>{count}</FilterActiveBadge>}
      </FilterIconWrap>
    </FilterSquareButton>
  );
};

const MainContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
});

// ChatArea wraps ChatInstance and the filter overlay; position:relative is required
// so the absolutely-positioned FilterOverlay is anchored inside it.
const ChatArea = styled('div')({
  position: 'relative',
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const TopicWrapper = styled('div')({
  padding: '10px 14px',
  flexShrink: 0,
});

// TopicRow holds the bordered context box (flex:1) and the New Chat circle (outside box)
const TopicRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const TopicInner = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid #E8E8EC',
  borderRadius: 8,
  padding: '0 12px',
  height: 46,
  gap: 8,
  flex: 1,
  minWidth: 0,
});

// Wraps SimpleContextSelector to allow it to flex-grow inside TopicInner.
// SimpleContextSelector does not forward className, so a wrapper div is used.
const ContextSelectorWrapper = styled('div')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
});

const StyledContextSearch = styled(ContextSearch)({
  flex: 1,
  minWidth: 0,
});

const TopicIconButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  '&:hover': { background: '#F0EFFC' },
});

const FilterSquareButton = styled('button')(() => ({
  background: '#565ADD',
  border: 'none',
  borderRadius: 7,
  width: 30,
  height: 30,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background 0.15s',
  outline: 'none',
  '&:hover': { background: '#4340a8' },
}));

const FilterIconWrap = styled('span')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

const FilterActiveBadge = styled('span')({
  position: 'absolute',
  top: -5,
  right: -5,
  minWidth: 14,
  height: 14,
  borderRadius: 7,
  backgroundColor: '#00B4AD',
  color: '#FFFFFF',
  fontSize: 9,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  padding: '0 2px',
  boxSizing: 'border-box',
});

const NewChatCircle = styled('button')({
  borderRadius: '50%',
  backgroundColor: '#4845B8',
  width: 30,
  height: 30,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  flexShrink: 0,
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    cursor: 'not-allowed',
  },
});

const StyledPlusIcon = styled(PlusIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  strokeWidth: 3,
  width: 18,
  minWidth: 18,
}));

// ── History panel ─────────────────────────────────────────────────────────────

const FullPanelView = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
});

const HistoryNewChatRow = styled('div')({
  padding: '16px 14px 18px',
  flexShrink: 0,
  borderBottom: '1px solid #E8E8EC',
});

const NewChatPill = styled('button')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  backgroundColor: '#4845B8',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 20,
  padding: '10px 16px',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    cursor: 'not-allowed',
  },
});

const HistoryScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
});

const HistoryFooter = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '10px 14px 16px',
  borderTop: '1px solid #E8E8EC',
  flexShrink: 0,
});

const SettingsGearButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: theme.palette.gray.main ?? '#999999',
  padding: 4,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  '&:hover': { color: theme.palette.sentiment.primary.main },
}));

// ── Settings panel ────────────────────────────────────────────────────────────

// ── Filter overlay panel ──────────────────────────────────────────────────────

// Absolutely positioned over the chat area, anchored to top of ChatArea.
// Height grows with content so the chat input bar stays visible below.
const FilterOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  background: '#FFFFFF',
  zIndex: 10,
  padding: '12px 14px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  overflow: 'visible',
});

const FilterViewHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  flexShrink: 0,
});

const FilterViewTitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  color: '#000000',
}));

const FilterResetButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  background: 'none',
  border: 'none',
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? '#C9C9C9' : '#A9A9A9',
  fontFamily: 'inherit',
  fontSize: 16,
  padding: '4px 0',
  flexShrink: 0,
  ...(!disabled && {
    '&:hover': {
      color: '#666666',
    },
    '&:active': {
      color: '#444444',
    },
    '&:focus-visible': {
      outline: '2px solid #A9A9A9',
      outlineOffset: 2,
      borderRadius: 2,
    },
  }),
}));

const SettingsContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '24px 20px',
});

const SettingsTitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 20,
  color: '#000000',
}));

const SettingsRow = styled('div')({
  fontSize: 16,
  color: '#000000',
});
